import React, { useState, useEffect, useCallback } from "react";
import { Employee, EmployeeSearch } from "../models";
import { TextField, Skeleton, Select, MenuItem, FormControl, InputLabel, Box, Paper } from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import orgDataService from "services/orgDataService";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { Hub } from "../../common/models";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridPaginationModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import employeeService from "services/employeeService";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";



const EmployeeList = () => {
  let persistedSearch = localStorage.getItem("employees_list");
  let initialSearch: EmployeeSearch = {
    firstName: "",
    lastName: "",
    eeCode: "",
    employeeNumber: "",
    status: "activeOnly",
    pagedSearchOptions: { offset: 0, pageSize: 25 },
    sortName: "lastName",
    sortAsc: true,
  };
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [search, setSearch] = useState<EmployeeSearch>(persistedSearch ? JSON.parse(persistedSearch): initialSearch);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const debounceSearch = useCallback(debounce(onSearch, 500), []);
  const gridRef = useGridApiRef();

  const columns: GridColDef[] = [
    {
      field: "employeeNumber",
      headerName: "Employee Number",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any, number>) => <Link to={`${params.row.id}`}>{params.value}</Link>,
    },
    { field: "lastName", headerName: "Last Name", flex: 1, cellClassName: "capitalize-first-letter",  renderCell: (params) => params.value?.toLowerCase()},
    { field: "firstName", headerName: "First Name", flex: 1, cellClassName: "capitalize-first-letter", renderCell: (params) => params.value?.toLowerCase()},
    { field: "title", headerName: "Title", flex: 1 },
    { field: "driverLocation", headerName: "Location", flex: 1 },
    { field: "hireDate", headerName: "Hire Date", flex:1},
    { field: "eightDayHOS", headerName: "8 Day", flex:1, sortable: false,
       renderCell: (params: GridRenderCellParams<any, boolean>)=><span>{params.value} / {params.row.eigthDayHOS}</span> },
    { field: "hos", headerName:"On Duty", width:90, sortable:false},
    { field: "driveTime", headerName: "Drive", width:90, sortable:false},
    { field: "sunSatHOS", headerName: "Sun-Sat", width:90, sortable:false},
    { field: "returnFromRest", headerName:"Return From Rest",sortable:false, 
      renderCell: (params: GridRenderCellParams<any, string>) =><>{params.value ? new Date(params.value).toLocaleDateString() : ''}</>},
    { field: "isActive", headerName: "Is Active", width: 90, align:"center",
      renderCell: (params: GridRenderCellParams<any, boolean>) =>
        params.value ? <CheckIcon color="success" /> : <CloseIcon color="error" /> },
    { field: "isDriver", headerName: "Is Driver", width: 90, align:"center", sortable: false,
      renderCell: (params: GridRenderCellParams<any, boolean>) =>
        params.value ? <CheckIcon color="success" /> : <CloseIcon color="error" /> },
  ];
  
  useEffect(() => {
    orgDataService.getAllHubs().then((data) => setHubs(data));
  }, []);

  useEffect(() => {
    if (isLoading) return;
    debounceSearch(search);
  }, [search]);

  function onSearch(request: EmployeeSearch) {
    setIsLoading(true);
    localStorage.setItem("employees_list", JSON.stringify(request));
    setEmployees([]);
    employeeService
      .searchEmployees(request)
      .then((results) => {
        setEmployees(results.results);
        setTotal(results.count);
      })
      .finally(() => setIsLoading(false));
  }

  function handleOnChange(e: any) {
    var request = {
      ...search,
      [e.target.name]: e.target.value,
      pagedSearchOptions: { pageSize: search.pagedSearchOptions.pageSize, offset: 0 },
    };
    setSearch(request);
    gridRef.current.setPage(0);
  }

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!(sortModel.length > 0)) return;
    var request = {
      ...search,
      sortName: sortModel[0].field,
      sortAsc: sortModel[0].sort == "asc",
      pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 },
    };
    setSearch(request);
    gridRef.current.setPage(0);
  };

  const handlePaginationModel = (pagination: GridPaginationModel) => {
    if (pagination.pageSize != search.pagedSearchOptions.pageSize) {
      pagination.page = 0;
    }
    setSearch({ ...search, pagedSearchOptions: { pageSize: pagination.pageSize, offset: pagination.page } });
  };

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(search.pagedSearchOptions.pageSize)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" width="98%" sx={{ my: 3, mx: 1, overflow: "hidden" }} />
      ))}
    </Box>
  );

  const clearFilters = ()=>{
    setSearch({...initialSearch});
  };

  return (
    <>
      <TextField
        sx={{ mt: 2, ml: 1 }}
        name="firstName"
        label="First Name"
        value={search.firstName}
        onChange={handleOnChange}
      />
      <TextField
        sx={{ mt: 2, ml: 1 }}
        name="lastName"
        label="Last Name"
        value={search.lastName}
        onChange={handleOnChange}
      />
      <TextField
        sx={{ mt: 2, ml: 1 }}
        name="eeCode"
        label="Employee Code"
        value={search.eeCode}
        onChange={handleOnChange}
      />
      <TextField
        sx={{ mt: 2, ml: 1 }}
        name="employeeNumber"
        label="Employee Number"
        value={search.employeeNumber}
        onChange={handleOnChange}
      />
      <FormControl sx={{ mt: 2, ml: 1,p: 0, minWidth: 120 }}>
        <InputLabel id="statusId-label">Status</InputLabel>
        <Select
        SelectDisplayProps={{ style: { padding: "0.75rem !important" as any } }}
          id="status"
          name="status"
          labelId="statusId-label"
          label="Status"
          value={search.status}
          onChange={handleOnChange}
          multiple={false}
          sx={{p:0}}
        >
          <MenuItem value="activeOnly">Active Only</MenuItem>
          <MenuItem value="terminated">Terminated</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </Select>
      </FormControl>
      <FormControl sx={{ mt: 2, ml: 1, minWidth: 120 }}>
        <InputLabel id="hubId">Hub</InputLabel>

        <Select id="hubId" name="hubId" labelId="hubId" label="hub" value={search.hubId} onChange={handleOnChange}>
          <MenuItem key={0} value=" ">
            {"All"}
          </MenuItem>
          {hubs &&
            hubs.map((h, index) => {
              return (
                <MenuItem key={index} value={h.id}>
                  {h.name}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
      <MDButton sx={{mt:2, ml:1}} onClick={clearFilters}>Clear</MDButton>
      <Box sx={{ mt: 2, overflow: "hidden" }}>
        <Paper sx={{ width: "99%", height: isLoading ? "100vh" : "auto", overflow: "hidden" }}>
          <DataGrid
            sx={{ overflow: "hidden", fontSize: ".95em" }}
            rows={employees}
            columns={columns}
            pagination
            sortingMode="server"
            paginationMode="server"
            onPaginationModelChange={handlePaginationModel}
            onSortModelChange={handleSortModelChange}
            initialState={{
              pagination: { paginationModel: search.pagedSearchOptions },
              sorting: { sortModel: [{ field: "lastName", sort: "asc" }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={total}
            disableColumnMenu
            slots={{
              loadingOverlay: LoadingSkeleton,
            }}
            loading={isLoading}
            density="compact"
            apiRef={gridRef as any}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
            }
          />
        </Paper>
      </Box>
    </>
  );
};

export default EmployeeList;
